import { useState, useEffect } from "react";
import { useSelector, useDispatch } from 'react-redux'
import { useParams, useHistory, Link } from "react-router-dom";
import { Card, Col, Container, Row, Spinner, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import Button from 'components/Inputs/TButton'

import AlertMessage from 'components/Alert'
import { alertActions } from 'redux/_actions'

import WebAPI from 'services/WebAPI'

import { generatePassword } from 'utils/functions'
import axios from "axios";


function DetailsLine({ title, value }) {
  return (
    <Row>
      <Col xs="3">
        <p className="card-sub-head">{title}</p>
      </Col>
      <Col xs="9">
        <p className="card-sub-value">{value}</p>
      </Col>
    </Row>
  )
}

const PopConfirm = ({ onOk }) => {
  const [modal, SetModal] = useState(false);

  const handleConfirm = () =>{
    if (onOk) onOk()
    SetModal(false)
  }

  return (
    <>
      <Button onClick={()=>SetModal(!modal)} color="danger" >
        <i className="far fa-trash-alt" />
      </Button>
      <Modal isOpen={modal} toggle={()=>SetModal(false)}>
        <ModalHeader
          className="confirm-modal-header"
          toggle={()=>SetModal(false)}
        >
          Are you sure to delete this customer?
        </ModalHeader>
        <ModalBody className="confirm-modal-body">
          As a result of this action, all endpoints, and users associated with this customer will be deleted from the system.
        </ModalBody>
        <ModalFooter className="confirm-modal-footer">
          <Button color="danger" size="sm" onClick={handleConfirm}>
            <i className="far fa-trash-alt" style={{ marginRight: 5 }} />
            Continue
          </Button>{" "}
          <Button size="sm" outline onClick={()=>SetModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

const CustomerDetails = () => {
  const [licenseData, setLicenseData] = useState([]);
  const [customer, setCustomer] = useState({});

  const [totals, setTotals] = useState({ servers: 0, desktops: 0 })
  const [consolePassword, setConsolePassword] = useState(null)
  const [download, setDownload] = useState(false);
  const [url, setUrl] = useState("");

  let history = useHistory();
  const { id } = useParams();

  const dispatch = useDispatch()

  useEffect(() => {
    fetchCustomerDetails()
  }, [id]);

  const fetchCustomerDetails = () => {
    dispatch(alertActions.clear())
    WebAPI
      .get(`/Customers/Details/${id}`)
      .then(({ data: res }) => {
        if (res.status) {
          setCustomer(res.data)
          setConsolePassword(res.consolePassword)
        }
        else{
          dispatch(alertActions.error(res.message === "Not allowed" ? "Customer not found" : res.message))
          setCustomer({})
          setConsolePassword(null)
        }
      })
      .then(() => {
        fetchLicenseList()
      })
  }

  const [loading, setLoading] = useState(false)

  const fetchLicenseList = () => {
    setLoading(true)
    WebAPI
      .get(`/Licenses/ListByCustomer/${id}`)
      .then(({ data: res }) => {
        if (res.status) {
          setLicenseData(res.data)
          totals.servers = res.data.reduce((prev, cur) => prev + cur.Server, 0)
          totals.desktops = res.data.reduce((prev, cur) => prev + cur.Desktop, 0)
        }
      })
      .finally(() => setLoading(false))
  }

  const [isSwitching, setSwitching] = useState(false)
  const switchState = (state) => {
    if (isSwitching)
      return;
    setSwitching(true)
    WebAPI
      .post(`/Customers/SwitchActive/${id}?isActive=${state ? 'true' : 'false'}`)
      .then(({ data: res }) => {
        if (res.status) {
          fetchCustomerDetails()
        }
      })
      .finally(() => setSwitching(false))
  }

  const sendConsolePassword = (password) => {
    if (isSwitching)
      return;
    setSwitching(true)
    WebAPI
      .post(`/Customers/SetConsolePassword/${id}`, { Password: password })
      .then(({ data: res }) => {
        if (res.status) {
          fetchCustomerDetails()
        }
      })
      .finally(() => setSwitching(false))
  }

  function generateConsolePassword() {
    setConsolePassword(null)
    sendConsolePassword(generatePassword(8))
  }

  function unsetConsolePassword() {
    setConsolePassword(null)
    sendConsolePassword("")
  }

  function handleDelete() {
    WebAPI.post(`/Customers/DeleteCustomer/${id}`).then(({ data: res }) => {
      if (res.status) {
        history.replace('/customers')
      }
    });
  }

  const prepareDownload = async (key) => {
    try {
      setDownload(true);
      setLoading(true);
      const resp = await axios.get(
        `https://installer.canauri.com/prepare_msi?c=${id}&key=${key}`
      );
      if (resp.data.status) setUrl(`https://installer.canauri.com${resp.data.url}`);
    } catch (err) {
      console.log("err", err);
    }
    setLoading(false);
  };

  const { role = "partner", theme = "white" } = useSelector(s => s.authentication.user) || {}

  const columnActions = (cell, row, rowIndex) => {
    return (
      <div style={{ display: "flex" }}>
        <Button
          outline
          size="sm"
          className="common-btn-outlined"
          color={theme === "white" ? "primary" : "secondary"}
          onClick={() =>
            history.push(`/customers/endpoints_under_license/${row.Key}`)
          }
        >
          <i class="fa fa-microchip" style={{ marginRight: 3 }} />
          Devices
        </Button>
        {(role === "admin" || role === "partner") && (
          <>
            <Button
              outline
              size="sm"
              className="common-btn-outlined"
              color="primary"
              onClick={() => prepareDownload(row.Key)}
            >
              <i className="fa fa-download" style={{ marginRight: 3 }} />
              Download
            </Button>
          </>
        )}
      </div>
    )
  }

  const columns = [
    {
      dataField: 'Key',
      text: 'Key',
      headerStyle: { width: '250px' },
    },
    {
      dataField: 'Server',
      text: 'Active Servers',
    }, {
      dataField: 'Desktop',
      text: 'Active Desktops',
    }, {
      dataField: 'ExpiryDate',
      text: 'Expiration Date',
      formatter: (cellContent) => new Date(cellContent).toLocaleDateString()
    }, {
      dataField: 'Type',
      text: 'Type'
    },
    {
      text: 'Actions',
      formatter: columnActions,
      headerStyle: { width: '300px' },
    }];

  return (
    <Container className="container-main" fluid>
      <div className="common-headers">
        <Button
          className="common-btn-back mr-2"
          outline
          onClick={() => history.go(-1)}
        >
          <i className="fas fa-chevron-left" />
        </Button>
        {customer.FirstName} {customer.LastName}
      </div>
      <AlertMessage />
      <Row>
        <Col xs="6">
          <div className="sub-header">Customer Details</div>
        </Col>
        <Col xs="6" style={{ display: "flex", justifyContent: "flex-end" }}>
          <PopConfirm onOk={handleDelete} />
          <Button
            color="primary"
            className="edit-btn common-btn"
            onClick={() => history.push("/customers/edit_customer/" + id)}
          >
            Edit Details
          </Button>
        </Col>
      </Row>
      <Row>
        <Col style={{ marginTop: "20px" }}>
          <Card>
            <Row>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                  padding: "10px 10px 0px 0px",
                }}
              >
                <Button
                  outline
                  className="common-btn-outlined"
                  color={theme === "white" ? "primary" : "secondary"}
                  type="button"
                  size="sm"
                  onClick={() =>
                    history.push(`/customers/list_endpoints/${id}`)
                  }
                >
                  Devices
                </Button>
                <Button
                  outline
                  className="common-btn-outlined"
                  color={theme === "white" ? "primary" : "secondary"}
                  type="button"
                  size="sm"
                  onClick={() =>
                    history.push("/customers/notification_settings/" + id)
                  }
                >
                  Notification Settings
                </Button>
                
                <Button
                  outline
                  className="mr-3 common-btn-outlined"
                  color={theme === "white" ? "primary" : "secondary"}
                  type="button"
                  size="sm"
                  onClick={() =>
                    history.push("/customers/customer_settings/" + id)
                  }
                >
                  Agent Default Settings
                </Button>
                <Button
                  className="mr-3"
                  outline={!customer.IsActive}
                  color={customer.IsActive ? "danger" : "primary"}
                  type="button"
                  size="sm"
                  onClick={() => switchState(!customer.IsActive)}
                  isLoading={isSwitching}
                >
                  {customer.IsActive ? "Deactivate" : "Activate"}
                </Button>
              </div>
            </Row>
            <Row style={{ padding: 10 }}>
              <Col xs="12">
                <DetailsLine
                  title="Name"
                  value={`${customer.FirstName} ${customer.LastName}`}
                />
                <DetailsLine
                  title="Partner Name"
                  value={customer.Partner?.EntityName}
                />
                <DetailsLine title="Email" value={customer.UserEmail} />
                <DetailsLine
                  title="Partner Contact"
                  value={customer.Partner?.Email}
                />
                <DetailsLine title="Company" value={customer.CompanyName} />
                <DetailsLine title="Servers Activated" value={totals.servers} />
                <DetailsLine
                  title="Desktop Activated"
                  value={totals.desktops}
                />
                {false && (
                  <DetailsLine
                    title="Console Password"
                    value={
                      <>
                        <span>
                          {consolePassword ? (
                            <strong>{consolePassword}</strong>
                          ) : (
                            <span style={{ color: "red" }}>
                              {isSwitching ? "<Working...>" : ""}
                            </span>
                          )}
                        </span>
                        <span className="ml-2">
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              generateConsolePassword();
                            }}
                          >
                            Generate
                          </a>
                        </span>
                        <span className="ml-2">
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              unsetConsolePassword();
                            }}
                          >
                            Remove
                          </a>
                        </span>
                      </>
                    }
                  />
                )}
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row style={{ marginTop: "20px" }}>
        <Col xs="6">
          <div className="sub-header">Customer Licenses</div>
        </Col>
        <Col xs="6">
          <Button
            color="primary"
            className="edit-btn"
            onClick={() => history.push(`/customers/new_license/${id}`)}
          >
            New License
          </Button>
        </Col>
      </Row>
      <Row style={{ marginTop: "20px", marginBottom: 20 }}>
        <Col xs="12">
          <ToolkitProvider
            keyField="PartnerId"
            data={licenseData}
            columns={columns}
          >
            {(props) => (
              <div>
                <BootstrapTable
                  hover
                  bordered={false}
                  noDataIndication={
                    loading ? (
                      <Spinner type="grow" color="primary" />
                    ) : (
                      "No Data"
                    )
                  }
                  headerWrapperClasses="table-header-light"
                  {...props.baseProps}
                  rowStyle={
                    {
                      //backgroundColor: '#fff',
                    }
                  }
                />
              </div>
            )}
          </ToolkitProvider>
        </Col>
      </Row>
      <Modal
        isOpen={download}
        toggle={() => {
          setUrl("");
          setDownload(false);
        }}
      >
        <ModalHeader>Download Installer</ModalHeader>
        <ModalBody style={{ textAlign: "center" }}>
          {loading && <Spinner />}
          {!loading && url && (
            <a href={url} target="_blank" rel="noreferrer">Click here to download!</a>
          )}
          {!loading && !url && <p>Something went wrong. Please try again!</p>}
        </ModalBody>
      </Modal>
    </Container>
  );
};

export default CustomerDetails;